import React, { useState, useEffect } from 'react';
import { activityLogsApi, uploadsApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { UploadHint, UPLOAD_ACCEPT_ATTR } from '../UploadHint';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { 
  Phone, 
  Mail, 
  Radio, 
  MessageSquare, 
  FileCheck, 
  AlertTriangle,
  MoreHorizontal,
  Upload,
  Send,
  Clock,
  Download
} from 'lucide-react';

const ACTIVITY_TYPES = [
  { value: 'phone_call', label: 'Phone Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'radio_message', label: 'Radio Message', icon: Radio },
  { value: 'verbal_communication', label: 'Verbal Communication', icon: MessageSquare },
  { value: 'decision', label: 'Decision', icon: FileCheck },
  { value: 'external_event', label: 'External Event', icon: AlertTriangle },
  { value: 'other', label: 'Other', icon: MoreHorizontal },
];

export default function ActivityLogForm({ exerciseId, onLogCreated }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    from_role: '',
    to_role: '',
    activity_type: 'phone_call',
    message: '',
    use_custom_time: false,
    custom_time: '',
  });
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadRoles();
  }, [exerciseId]);

  const loadRoles = async () => {
    try {
      const data = await activityLogsApi.getRoles(exerciseId);
      setRoles(data);
    } catch (error) {
      console.error('Failed to load roles:', error);
      // Set default roles if API fails
      setRoles([
        { value: 'simcell', label: 'Simcell' },
        { value: 'exercise_controller', label: 'Exercise Controller' },
        { value: 'participant', label: 'Participant' },
        { value: 'observer', label: 'Observer' },
        { value: 'external_agency', label: 'External Agency' },
        { value: 'media_cell', label: 'Media Cell' },
        { value: 'operations', label: 'Operations' },
        { value: 'planning', label: 'Planning' },
        { value: 'logistics', label: 'Logistics' },
        { value: 'finance', label: 'Finance' },
      ]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.from_role || !formData.message.trim()) {
      toast.error('Please fill in required fields (From Role and Message)');
      return;
    }

    setLoading(true);
    
    try {
      // Upload attachment first if present
      let attachmentIds = [];
      if (attachment) {
        setUploading(true);
        const uploadResult = await uploadsApi.upload(attachment, exerciseId);
        attachmentIds = [uploadResult.file_id];
        setUploading(false);
      }

      const logData = {
        exercise_id: exerciseId,
        from_role: formData.from_role,
        to_role: formData.to_role || null,
        activity_type: formData.activity_type,
        message: formData.message,
        attachment_ids: attachmentIds.length > 0 ? attachmentIds : null,
        scenario_time: formData.use_custom_time && formData.custom_time 
          ? new Date(formData.custom_time).toISOString() 
          : null,
      };

      await activityLogsApi.create(logData);
      
      toast.success('Activity log entry posted');
      
      // Reset form
      setFormData({
        from_role: formData.from_role, // Keep from_role for convenience
        to_role: '',
        activity_type: 'phone_call',
        message: '',
        use_custom_time: false,
        custom_time: '',
      });
      setAttachment(null);
      
      if (onLogCreated) {
        onLogCreated();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create log entry');
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const selectedTypeIcon = ACTIVITY_TYPES.find(t => t.value === formData.activity_type)?.icon || MessageSquare;
  const TypeIcon = selectedTypeIcon;

  return (
    <Card className="border-dashed" data-testid="activity-log-form">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TypeIcon className="w-4 h-4" />
            Post Activity Log
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => activityLogsApi.exportCsv(exerciseId)}
            className="h-7 text-xs gap-1" data-testid="export-activity-logs-btn">
            <Download className="w-3 h-3" /> Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Activity Type */}
          <div className="space-y-1">
            <Label className="text-xs">Activity Type</Label>
            <Select 
              value={formData.activity_type} 
              onValueChange={(v) => setFormData({...formData, activity_type: v})}
            >
              <SelectTrigger className="h-9" data-testid="activity-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* From / To Roles */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">From Role *</Label>
              <Input
                value={formData.from_role}
                onChange={(e) => setFormData({...formData, from_role: e.target.value})}
                placeholder="Type or select..."
                list="from-role-options"
                className="h-9"
                data-testid="from-role-input"
              />
              <datalist id="from-role-options">
                {roles.map(role => (
                  <option key={role.value} value={role.label} />
                ))}
              </datalist>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To Role</Label>
              <Input
                value={formData.to_role}
                onChange={(e) => setFormData({...formData, to_role: e.target.value})}
                placeholder="(Optional)"
                list="to-role-options"
                className="h-9"
                data-testid="to-role-input"
              />
              <datalist id="to-role-options">
                {roles.map(role => (
                  <option key={role.value} value={role.label} />
                ))}
              </datalist>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-1">
            <Label className="text-xs">Message / Description *</Label>
            <Textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="Enter activity details..."
              className="min-h-[80px] resize-none"
              data-testid="activity-message-input"
            />
          </div>

          {/* Custom Time Option */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="use_custom_time"
              checked={formData.use_custom_time}
              onChange={(e) => setFormData({...formData, use_custom_time: e.target.checked})}
              className="rounded border-border"
            />
            <Label htmlFor="use_custom_time" className="text-xs cursor-pointer flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Custom timestamp
            </Label>
            {formData.use_custom_time && (
              <Input
                type="datetime-local"
                value={formData.custom_time}
                onChange={(e) => setFormData({...formData, custom_time: e.target.value})}
                className="h-8 text-xs flex-1"
              />
            )}
          </div>

          {/* Attachment */}
          <div className="space-y-1">
            <Label className="text-xs">Attachment (optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept={UPLOAD_ACCEPT_ATTR}
                onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                className="h-9 text-xs"
                data-testid="activity-attachment-input"
              />
              {attachment && (
                <span className="text-xs text-muted-foreground truncate max-w-[100px]">
                  {attachment.name}
                </span>
              )}
            </div>
            <UploadHint />
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading || uploading}
            data-testid="post-activity-log-btn"
          >
            {uploading ? (
              <>
                <Upload className="w-4 h-4 mr-2 animate-pulse" />
                Uploading...
              </>
            ) : loading ? (
              'Posting...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Post Log Entry
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
